<template>
  <div>
    <div v-if="renderedBoard">
      <component :is="BOARDS[renderedBoard]" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { trackMobileWebEvent } from "@/lib/pendo";
import { useResolvedFeatureFlag } from "@/lib/useFeatureFlag";

import SchedulePage from "@/Scheduler/SchedulePage.vue";
import SchedulePageV2 from "@/SchedulerV2/SchedulePageV2.vue";
import { useSchedulerChoice } from "@/SchedulerV2/useSchedulerChoice";

const BOARDS = { v1: SchedulePage, v2: SchedulePageV2 };

const store = useStore();
const { scheduler2Enabled } = useSchedulerChoice();

// `resolved` holds both boards back until the tenant answers, as FeatureFlag.vue
// does: the bare flag reads false while loading, which would paint (and report)
// Old Scheduler before swapping.
const { enabled: schedulerFeatureFlag, resolved: schedulerFlagResolved } =
  useResolvedFeatureFlag("scheduler_v2_ff_enabled");
const preferencesLoaded = ref(false);

// The one place the board is chosen; the template and the Pendo view both read
// it. Derived per render rather than captured once: `scheduler2Enabled` reads
// whichever preference the current viewport class owns, so crossing the md
// breakpoint re-picks the board instead of leaving the class the page happened
// to load at in charge of it. Held back until the fetch lands because an
// absent row reads as "off", which would paint Old Scheduler and then swap.
const renderedBoard = computed<keyof typeof BOARDS | null>(() => {
  if (!schedulerFlagResolved.value) return null;
  if (!schedulerFeatureFlag.value) return "v1";
  if (!preferencesLoaded.value) return null;
  return scheduler2Enabled.value ? "v2" : "v1";
});

watch(
  renderedBoard,
  (board) => {
    if (!board) return;
    trackMobileWebEvent("schedule_view", {
      board,
      scheduler_v2_available: schedulerFeatureFlag.value
    });
  },
  { immediate: true }
);

const fetchAdminPreferences = async () => {
  await store.dispatch("fetchPreferences");
  preferencesLoaded.value = true;
};

watch(
  schedulerFeatureFlag,
  (enabled) => {
    if (enabled) fetchAdminPreferences();
  },
  { immediate: true }
);
onMounted(() => {
  if (schedulerFeatureFlag.value) {
    fetchAdminPreferences();
  }
});
</script>
<style>
.scheduler2-modal-img {
  position: relative;
  left: -30px;
  width: calc(100% + 60px) !important;
  max-width: none;
}
</style>
